import {isFeatureEnabled} from 'feature-flags';
import {match} from 'ts-pattern';

import {isEmpty, isNil, trim} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {AdServer} from '../types/AdServer';
import {FilterFormData} from '../types/FilterFormData';
import {featureFlags} from './featureFlags';
import {isUnsupportedCombination} from './isUnsupportedCombination';

const VALID_AD_SERVERS: readonly string[] = Object.values(AdServer);

const isAdServer = (value: string): value is AdServer => VALID_AD_SERVERS.includes(value);

const parseServerSearchParam = (value: string | null): AdServer[] | undefined => {
  if (isNil(value) || isEmpty(value) || !isFeatureEnabled(featureFlags.PR_MULTI_SERVERS)) {
    return undefined;
  }
  const servers = value.split(',').map(trim).filter(isAdServer);
  return isEmpty(servers) ? undefined : servers;
};

export const parseSearchParamsToFilterFormData = (
  searchParams: URLSearchParams
): FilterFormData => ({
  address:
    isNotNilOrEmpty(searchParams.get('label')) &&
    isNotNilOrEmpty(searchParams.get('lat')) &&
    isNotNilOrEmpty(searchParams.get('lng'))
      ? {
          label: searchParams.get('label') as string,
          lat: Number(searchParams.get('lat')),
          lng: Number(searchParams.get('lng')),
        }
      : null,
  radius: isNotNilOrEmpty(searchParams.get('radius')) ? Number(searchParams.get('radius')) : null,
  title: isNotNilOrEmpty(searchParams.get('title')) ? searchParams.get('title') : null,
  showStockCars: match(searchParams.get('showStockCars'))
    .with('true', () => true)
    .with('false', () => false)
    .otherwise(() => null),
  showSoldIn30: match(searchParams.get('showSoldIn30'))
    .with('true', () => true)
    .with('false', () => false)
    .otherwise(() => null),
  showSoldIn90: match({
    soldIn90: searchParams.get('showSoldIn90'),
    isUnsupportedCombination: isUnsupportedCombination(
      searchParams.get('showStockCars') === 'true',
      searchParams.get('showSoldIn30') === 'true',
      searchParams.get('showSoldIn90') === 'true'
    ),
  })
    .with({isUnsupportedCombination: true}, () => false)
    .with({soldIn90: 'true'}, () => true)
    .with({soldIn90: 'false'}, () => false)
    .otherwise(() => null),
  priceGte: isNotNilOrEmpty(searchParams.get('priceGte'))
    ? Number(searchParams.get('priceGte'))
    : null,
  priceLte: isNotNilOrEmpty(searchParams.get('priceLte'))
    ? Number(searchParams.get('priceLte'))
    : null,
  isVatDeductible: match<string | null, ('YES' | 'NO')[]>(searchParams.get('isVatDeductible'))
    .with('YES', () => ['YES'])
    .with('NO', () => ['NO'])
    .otherwise(() => []),
  firstRegistrationOnYearGte: isNotNilOrEmpty(searchParams.get('firstRegistrationOnYearGte'))
    ? Number(searchParams.get('firstRegistrationOnYearGte'))
    : null,
  firstRegistrationOnYearLte: isNotNilOrEmpty(searchParams.get('firstRegistrationOnYearLte'))
    ? Number(searchParams.get('firstRegistrationOnYearLte'))
    : null,
  powerGte: isNotNilOrEmpty(searchParams.get('powerGte'))
    ? Number(searchParams.get('powerGte'))
    : null,
  powerLte: isNotNilOrEmpty(searchParams.get('powerLte'))
    ? Number(searchParams.get('powerLte'))
    : null,
  mileageGte: isNotNilOrEmpty(searchParams.get('mileageGte'))
    ? Number(searchParams.get('mileageGte'))
    : null,
  mileageLte: isNotNilOrEmpty(searchParams.get('mileageLte'))
    ? Number(searchParams.get('mileageLte'))
    : null,
  daysInAdvertisementGte: isNotNilOrEmpty(searchParams.get('daysInAdvertisementGte'))
    ? Number(searchParams.get('daysInAdvertisementGte'))
    : null,
  daysInAdvertisementLte: isNotNilOrEmpty(searchParams.get('daysInAdvertisementLte'))
    ? Number(searchParams.get('daysInAdvertisementLte'))
    : null,
  featureScoreGte: isNotNilOrEmpty(searchParams.get('featureScoreGte'))
    ? Number(searchParams.get('featureScoreGte'))
    : null,
  featureScoreLte: isNotNilOrEmpty(searchParams.get('featureScoreLte'))
    ? Number(searchParams.get('featureScoreLte'))
    : null,
  drive: isNotNilOrEmpty(searchParams.get('drive'))
    ? (searchParams.get('drive') as string).split(',')
    : [],
  transmission: isNotNilOrEmpty(searchParams.get('transmission'))
    ? (searchParams.get('transmission') as string).split(',')
    : [],
  bodyStyle: isNotNilOrEmpty(searchParams.get('bodyStyle'))
    ? (searchParams.get('bodyStyle') as string).split(',')
    : [],
  fuelType: isNotNilOrEmpty(searchParams.get('fuelType'))
    ? (searchParams.get('fuelType') as string).split(',')
    : [],
  trimLevels: isNotNilOrEmpty(searchParams.get('trimLevels'))
    ? (searchParams.get('trimLevels') as string).split(',')
    : [],
  sellerType: isNotNilOrEmpty(searchParams.get('sellerType'))
    ? (searchParams.get('sellerType') as string).split(',')
    : [],
  server: parseServerSearchParam(searchParams.get('server')),
});
